import { cn } from '@/lib/utils';

export interface BrandMarkProps {
  /** Mark size: `sm` = 28px, `md` = 32px. */
  size?: 'sm' | 'md';
  /**
   * `true` (default) always shows the wordmark; `false` shows the mark alone;
   * `'lg'` shows the wordmark only at `lg+` (the rail, where it is icon-only
   * below).
   */
  showWordmark?: boolean | 'lg';
  className?: string;
}

/**
 * The mark + "ScreenAtlas" wordmark lockup (ui-ux.md §3). The wordmark is set
 * in `text-wordmark` (19px/24px/700/−0.02em) on the display family (`font-display`,
 * applied here because the display-family utility list lives in index.css).
 *
 * When the wordmark is *unconditionally* visible it carries the name and the
 * mark is decorative (`alt=""`); when the wordmark is hidden (or hidden below
 * `lg`) the mark carries the name (`alt="ScreenAtlas"`) so the lockup always
 * has an accessible name at every breakpoint.
 */
export function BrandMark({ size = 'md', showWordmark = true, className }: BrandMarkProps) {
  const px = size === 'sm' ? 28 : 32;
  const markClass = size === 'sm' ? 'h-7 w-7' : 'h-8 w-8';
  const wordmarkBelowLg = showWordmark === 'lg';
  const wordmarkAlways = showWordmark === true;
  // Only when the wordmark is always visible do we rely on it for the name.
  const alt = wordmarkAlways ? '' : 'ScreenAtlas';

  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <img
        src={`${import.meta.env.BASE_URL}mark.png`}
        alt={alt}
        width={px}
        height={px}
        className={cn('shrink-0 object-contain', markClass)}
      />
      {showWordmark !== false ? (
        // Built with a plain template string, not `cn`: tailwind-merge does
        // not know the custom `text-wordmark` size and would drop it (or the
        // colour) as a `text-*` conflict.
        <span
          aria-hidden={wordmarkAlways ? undefined : 'true'}
          className={`font-display text-wordmark text-ink${wordmarkBelowLg ? ' hidden lg:inline' : ''}`}
        >
          ScreenAtlas
        </span>
      ) : null}
    </span>
  );
}
